import { useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import { isLoading, readyData } from '@/lib/readParts';
import { useNavigate } from '@tanstack/react-router';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ApiError } from '@signalvote/api-client';
import { ErrorCode } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { useMayManageCampaign } from '@/hooks';
import { useBillingConfig, useSubscribe } from '@/hooks/queries';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, ArrowLeft } from 'lucide-react';

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setError(null);
    setIsConfirming(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/subscribe/complete',
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message ?? 'Payment failed. Please try again.');
      } else {
        navigate({ to: '/subscribe/complete' });
      }
    } catch {
      setError('Failed to create subscription. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />

      {error && (
        <p className="mt-4 text-sm text-red-600">{error}</p>
      )}

      <Button
        type="submit"
        disabled={!stripe || isConfirming}
        className="w-full mt-6"
      >
        {isConfirming ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="mr-2 h-4 w-4" />
            Subscribe
          </>
        )}
      </Button>
    </form>
  );
}

export function SubscribePage() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const selectCampaign = useCampaignStore((s) => s.selectCampaign);
  const navigate = useNavigate();
  const mayManageBilling = useMayManageCampaign();

  const configRead = useBillingConfig();
  const config = readyData(configRead);
  const configLoading = isLoading(configRead);
  const configError = configRead.status === 'failed' || configRead.status === 'offline';
  const { mutateAsync: subscribe, isPending: subscribePending, data: subscribeData } = useSubscribe();
  const [subscribeFailure, setSubscribeFailure] = useState<'alreadySubscribed' | 'failed' | null>(null);
  const nothingDue = subscribeData !== undefined && !subscribeData.clientSecret;

  const stripePromise = useMemo(
    () => (config ? loadStripe(config.publishableKey) : null),
    [config?.publishableKey],
  );

  // No campaign selected → redirect to /app
  useEffect(() => {
    if (!selectedCampaign) {
      navigate({ to: '/app' });
    }
  }, [selectedCampaign, navigate]);

  // Auto-create subscription once config is loaded to get clientSecret.
  // Fires once per mount: a failed attempt must not retry on its own (a 409 would loop).
  const subscribeCalledRef = useRef(false);
  useEffect(() => {
    if (mayManageBilling && config && selectedCampaign && !subscribeData && !subscribePending && !subscribeCalledRef.current) {
      subscribeCalledRef.current = true;
      subscribe().catch((error) => {
        setSubscribeFailure(
          error instanceof ApiError && error.code === ErrorCode.BillingAlreadySubscribed ? 'alreadySubscribed' : 'failed',
        );
      });
    }
  }, [mayManageBilling, config, selectedCampaign, subscribeData, subscribePending, subscribe]);

  useEffect(() => {
    if (nothingDue) {
      navigate({ to: '/subscribe/complete' });
    }
  }, [nothingDue, navigate]);

  if (!selectedCampaign) {
    return null;
  }

  const handleSwitchCampaign = () => {
    selectCampaign(null);
    navigate({ to: '/app' });
  };

  if (!mayManageBilling) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Ask your campaign to subscribe</CardTitle>
            <CardDescription>{selectedCampaign.name}</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-gray-500">
              Only the campaign owner or a full admin can subscribe.
            </p>
            <Button variant="outline" onClick={handleSwitchCampaign}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to campaigns
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (configLoading || subscribePending || nothingDue) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-600" />
          <p className="text-sm text-gray-500">Loading billing information...</p>
        </div>
      </div>
    );
  }

  if (subscribeFailure === 'alreadySubscribed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>This campaign already has a subscription</CardTitle>
            <CardDescription>{selectedCampaign.name}</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-gray-500">
              There's nothing to pay here. You can manage the subscription from billing settings.
            </p>
            <Button className="w-full" onClick={() => navigate({ to: '/app/settings/billing' })}>
              Go to billing
            </Button>
            <Button variant="outline" onClick={handleSwitchCampaign}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to campaigns
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (configError || !config || subscribeFailure) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-sm text-red-600">
              Unable to load billing information. Please try again later.
            </p>
            <Button variant="outline" onClick={handleSwitchCampaign}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to campaigns
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const clientSecret = subscribeData?.clientSecret;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Subscribe</CardTitle>
          <CardDescription>{selectedCampaign.name}</CardDescription>
          {config.discount ? (
            <div className="mt-2 space-y-1">
              <p className="text-sm text-gray-500 line-through">
                {formatPrice(config.amount, config.currency)}/{config.interval}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(config.discount.netAmount, config.currency)}/{config.interval}
              </p>
              <p className="text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full inline-block px-3 py-1">
                You're saving {formatPrice(config.discount.amountOff, config.currency)}/
                {config.interval} as a beta partner
              </p>
            </div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {formatPrice(config.amount, config.currency)}/{config.interval}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {stripePromise && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm />
            </Elements>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleSwitchCampaign}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Switch campaign
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
